import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  expireTestCampaign,
  expireTestCommitments,
  fetchTestCampaign,
  fetchTestCampaigns,
  fetchTestPostpaidStatus,
  fetchTestTopupStatus,
  placeTestCharityOrders,
  purgeTestCampaign,
  recordTestInvoicePayment,
  renameTestCampaign,
  runTesterAction,
  runTestPostpaidSweep,
  runTestTopup,
  seedTestCampaign,
} from "@/api/campaignTester";
import type {
  SeedCampaignPayload,
  TesterAction,
  TesterEventPayload,
} from "@/interfaces/campaignTester";

export function useTestCampaigns() {
  return useQuery({ queryKey: ["campaign-tester", "campaigns"], queryFn: fetchTestCampaigns });
}

export function useTestCampaign(campaignId: number | null) {
  return useQuery({
    queryKey: ["campaign-tester", "campaign", campaignId],
    queryFn: () => fetchTestCampaign(campaignId as number),
    enabled: campaignId != null,
  });
}

export function useSeedTestCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SeedCampaignPayload) => seedTestCampaign(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-tester"] }),
  });
}

export function usePurgeTestCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: number) => purgeTestCampaign(campaignId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-tester"] }),
  });
}

export function useRenameTestCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, title }: { campaignId: number; title: string }) =>
      renameTestCampaign(campaignId, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-tester"] }),
  });
}

/**
 * Close a campaign: the tester winds its clock to the simulated date, the real close runs.
 *
 * Invalidates the journal-entry queries too — a donation posts, and the drawer's entries panel is a
 * separate query that nothing else refreshes.
 */
export function useExpireTestCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, occurredAt }: { campaignId: number; occurredAt?: string }) =>
      expireTestCampaign(campaignId, occurredAt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}

/** Force the recipient windows shut and sweep. Moves credits and may post, so refresh both trees. */
export function useExpireTestCommitments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, occurredAt }: { campaignId: number; occurredAt?: string }) =>
      expireTestCommitments(campaignId, occurredAt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}

/**
 * Buy the charity products for this campaign's donations.
 *
 * Invalidates the tester tree so the donation panel picks up each line's new order id and status —
 * "Pending order" becoming "Order created" is the entire point of pressing it.
 */
export function usePlaceTestCharityOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: number) => placeTestCharityOrders(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
    },
  });
}

/**
 * One lifecycle event. Invalidates the whole tester tree rather than one campaign: an action moves
 * credits, the budget AND the ledger, so a narrower invalidation would leave the screen showing
 * numbers that disagree with each other.
 */
export function useTesterAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ...payload }: TesterEventPayload & { action: TesterAction }) =>
      runTesterAction(action, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      // The entries panel is a SEPARATE query (["relatedJournalEntries", sourceType, sourceId]) and
      // nothing else refreshes it. Without this, dispatching with the drawer open leaves it showing
      // the list it fetched before the action — the redemption alone, with no sign of the
      // fulfilment entry that was just posted. Redemption and dispatch both write journal entries,
      // so the panel is as stale as the numbers after every action that posts.
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}

/**
 * Where an automated campaign stands against its margin. Cheap and read-only, so it re-fetches on
 * focus: the sweep can also run from the real hourly cron, and a panel that quietly disagrees with
 * the database is worse than one that flickers.
 */
export function useTestTopupStatus(campaignId: number | null) {
  return useQuery({
    queryKey: ["campaign-tester", "topup-status", campaignId],
    queryFn: () => fetchTestTopupStatus(campaignId as number),
    enabled: campaignId != null,
    refetchOnWindowFocus: true,
  });
}

/**
 * Run the sweep against one campaign. Invalidates the whole tester tree, not just the status: a
 * call mints a lot and moves the budget, so the credit strip and the ledger are as stale as the
 * margin figures.
 */
export function useRunTestTopup(campaignId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: { dryRun?: boolean; decline?: string }) => runTestTopup(campaignId, opts),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}

/** Where a postpaid campaign stands. Re-fetches on focus: the real daily sweep can move it too. */
export function useTestPostpaidStatus(campaignId: number | null) {
  return useQuery({
    queryKey: ["campaign-tester", "postpaid-status", campaignId],
    queryFn: () => fetchTestPostpaidStatus(campaignId as number),
    enabled: campaignId != null,
    refetchOnWindowFocus: true,
  });
}

/** Run the postpaid sweep for one campaign. An invoice is raised, so the whole tree is stale. */
export function useRunTestPostpaidSweep(campaignId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (occurredAt?: string) => runTestPostpaidSweep(campaignId, occurredAt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}

/** Record an invoice paid. Posts the settlement and marks the required payment paid. */
export function useRecordTestInvoicePayment(campaignId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: number) => recordTestInvoicePayment(campaignId, invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-tester"] });
      qc.invalidateQueries({ queryKey: ["relatedJournalEntries"] });
    },
  });
}
